import React, { useMemo, useState } from 'react'
import SignaturePad from '../components/SignaturePad'

// FORM B — the treatment-plan consent, as the patient sees it on their phone.
//
// The shape of this page is not a design choice; it is what 17-consent-wording.md §4 and the
// e-signature standard require:
//   * the plan itself, per visit, per tooth, per code — they sign against what is planned,
//     not against a number;
//   * the total stated as a QUOTE, never a price;
//   * the risks, and the alternatives ENDING with what happens if they do nothing;
//   * seven SEPARATE acknowledgements — a single "I agree to everything" tick is evidence of
//     a click, not of a conversation (S-4.1);
//   * a typed full name, which is the legally operative signature (ECTA s13(3)), with the
//     drawn stroke alongside it.

const money = (n) => `R${(Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-brand-surface px-4 py-6">
      <div className="mx-auto w-full max-w-xl">{children}</div>
    </div>
  )
}

function Centered({ emoji, title, body }) {
  return (
    <Shell>
      <div className="rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-brand-border">
        <div className="text-4xl">{emoji}</div>
        <h1 className="mt-3 text-lg font-semibold text-brand-ink">{title}</h1>
        <p className="mt-2 text-sm text-brand-muted">{body}</p>
      </div>
    </Shell>
  )
}

const CAPACITIES = [
  { value: 'self', label: 'I am the patient' },
  { value: 'parent', label: 'I am the patient’s parent' },
  { value: 'guardian', label: 'I am the patient’s legal guardian' },
  { value: 'care_giver', label: 'I am the patient’s care-giver' },
]

export default function PublicConsent(props) {
  const {
    invalid, already_signed, signed_at, practice = {}, consent = {}, patient = {},
    plan = null, acknowledgements = {}, co_signature_required = false, surgical = false,
  } = props

  const [acks, setAcks] = useState({})
  const [sig, setSig] = useState({ signed_name: '', signer_id_number: '', signer_capacity: 'self', signer_relationship: '', co_signer_name: '', language: 'en', interpreter_name: '' })
  const [stroke, setStroke] = useState('')
  const [strokeMetrics, setStrokeMetrics] = useState(null)
  const [photoRecord, setPhotoRecord] = useState(false)
  const [photoMarketing, setPhotoMarketing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(false)

  const ackKeys = Object.keys(acknowledgements)
  const allTicked = useMemo(() => ackKeys.length > 0 && ackKeys.every((k) => acks[k]), [acks, ackKeys])

  // Children's Act s129(5) — a care-giver may consent to ordinary treatment but NOT to surgery.
  const caregiverBlocked = surgical && sig.signer_capacity === 'care_giver'

  if (invalid) return <Centered emoji="🔒" title="This link is no longer valid" body="Consent links expire after 30 days. Please contact the practice and we'll send you a fresh one." />
  if (already_signed) return <Centered emoji="✅" title="Already signed" body={`You signed this on ${signed_at}. There's nothing further to do — a copy is in your file and we can send it to you any time.`} />
  if (done) return <Centered emoji="✅" title="Thank you — that's signed" body="We've filed your signed consent. We'll go through it with you again on the day before we start." />

  const submit = async () => {
    if (!allTicked) { setError('Please tick every point above — each one is a separate confirmation.'); return }
    if (sig.signed_name.trim().length < 3) { setError('Please type your full name — that is what signs this form.'); return }
    if (!stroke) { setError('Please sign your name in the box — a tap or a small mark is not a signature.'); return }
    if (sig.signer_capacity !== 'self' && !sig.signer_relationship.trim()) { setError('Please say how you are related to the patient.'); return }
    if (caregiverBlocked) { setError('A parent or legal guardian has to consent to this procedure. Please contact the practice.'); return }
    if (co_signature_required && !sig.co_signer_name.trim()) { setError('A parent or guardian must also sign for this procedure.'); return }

    setBusy(true); setError(null)
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ''
      const res = await fetch(`${window.location.pathname}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ acknowledgements: acks, signature: sig, signature_data: stroke,
          signature_metrics: strokeMetrics, photo_clinical_record: photoRecord, photo_marketing: photoMarketing }),
        credentials: 'same-origin',
      })
      const body = await res.json().catch(() => ({}))
      if (res.ok && body.ok) { setDone(true); window.scrollTo(0, 0) }
      else { setError(body.error || 'Something went wrong. Please try again.'); setBusy(false) }
    } catch {
      setError('We couldn’t reach the server. Please check your connection and try again.'); setBusy(false)
    }
  }

  return (
    <Shell>
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary">{practice.name}</p>
      <h1 className="mt-1 text-xl font-semibold text-brand-ink">Consent to treatment</h1>
      <p className="mt-1 text-sm text-brand-muted">
        {patient.first_name ? `For ${patient.first_name}. ` : ''}Please read this through — it is what we will do,
        what could go wrong, and what else you could choose. We will go through it with you again on the day.
      </p>

      {/* The plan itself */}
      {plan && (
        <div className="mt-5 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-brand-border">
          <h2 className="text-sm font-semibold text-brand-ink">{plan.title || 'What is planned'}</h2>
          {plan.visits?.map((v) => (
            <div key={v.visit} className="mt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Visit {v.visit}</p>
              <ul className="mt-1 divide-y divide-brand-border/60">
                {v.items.map((it, i) => (
                  <li key={i} className="flex items-start justify-between gap-3 py-1.5 text-sm">
                    <span className="text-brand-ink">
                      {it.description}
                      {it.tooth && <span className="ml-1 font-mono text-xs text-brand-muted">tooth {it.tooth}</span>}
                      <span className="ml-1 font-mono text-[11px] text-brand-muted">{it.code}</span>
                    </span>
                    <span className="whitespace-nowrap text-brand-ink">{money(it.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="mt-4 flex items-baseline justify-between border-t border-brand-border pt-3">
            <span className="text-sm font-semibold text-brand-ink">Estimated total</span>
            <span className="text-lg font-bold text-brand-ink">{money(plan.total)}</span>
          </div>
          <p className="mt-1 text-xs text-brand-muted">{plan.total_note}</p>
        </div>
      )}

      {/* Risks and alternatives */}
      {plan?.procedures?.map((p) => (
        <div key={p.key} className="mt-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-brand-border">
          <h3 className="text-sm font-semibold text-brand-ink">{p.label}</h3>
          <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-brand-muted">What can go wrong</p>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-brand-ink">
            {p.risks.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
          <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-brand-muted">What else you could do</p>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-brand-ink">
            {/* the last item is always the option of no treatment — enforced server-side */}
            {p.alternatives.map((a, i) => (
              <li key={i} className={i === p.alternatives.length - 1 ? 'font-semibold' : undefined}>{a}</li>
            ))}
          </ul>
        </div>
      ))}

      {plan?.unmatched_codes?.length > 0 && (
        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          Some items on your plan are not covered by the notes above. We will go through those with you in person
          before we start.
        </div>
      )}

      {/* The seven separate acknowledgements */}
      <div className="mt-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-brand-border">
        <h2 className="text-sm font-semibold text-brand-ink">Please confirm each of these</h2>
        <p className="mt-1 text-xs text-brand-muted">Each one is separate — please tick them individually.</p>
        <div className="mt-3 space-y-2">
          {ackKeys.map((k) => (
            <label key={k} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${acks[k] ? 'border-brand-primary bg-brand-surface/60' : 'border-brand-border'}`}>
              <input type="checkbox" checked={!!acks[k]} onChange={(e) => setAcks((p) => ({ ...p, [k]: e.target.checked }))} className="mt-0.5 h-4 w-4" />
              <span className="text-sm text-brand-ink">{acknowledgements[k]}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Photographs — deliberately separate and optional (POPIA s11: bundling makes it involuntary) */}
      <div className="mt-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-brand-border">
        <h2 className="text-sm font-semibold text-brand-ink">Photographs <span className="font-normal text-brand-muted">(optional)</span></h2>
        <p className="mt-1 text-xs text-brand-muted">Your treatment is exactly the same whether you agree to these or not.</p>
        <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-xl border border-brand-border p-3">
          <input type="checkbox" checked={photoRecord} onChange={(e) => setPhotoRecord(e.target.checked)} className="mt-0.5 h-4 w-4" />
          <span className="text-sm text-brand-ink">Photographs may be taken <b>for my clinical record</b> <span className="text-brand-muted">(recommended — part of good record-keeping)</span></span>
        </label>
        <label className="mt-2 flex cursor-pointer items-start gap-3 rounded-xl border border-brand-border p-3">
          <input type="checkbox" checked={photoMarketing} onChange={(e) => setPhotoMarketing(e.target.checked)} className="mt-0.5 h-4 w-4" />
          <span className="text-sm text-brand-ink">And they may be used on the practice’s <b>website and social media</b> — completely optional, and <b>I may withdraw this at any time</b></span>
        </label>
      </div>

      {/* Signature */}
      <div className="mt-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-brand-border">
        <h2 className="text-sm font-semibold text-brand-ink">Sign</h2>

        {/* NHA s6(2): the provider must, where possible, inform the patient "in a language that
            the user understands". HPCSA Booklet 4 says the same and points to translations or
            independent interpreters. The law requires UNDERSTANDING, not a particular form — so
            the honest thing is to ask which language the patient wants this explained in, record
            it, and say plainly that the written form is in English. 17 §9 is explicit that
            Afrikaans wording is a SEPARATE reviewed template version, never a runtime
            translation, so this never machine-translates clinical risk wording. */}
        <label className="block text-sm font-medium text-brand-ink">Which language would you like this explained in?</label>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {[{ v: 'en', l: 'English' }, { v: 'af', l: 'Afrikaans' }].map((o) => (
            <label key={o.v} className={`flex cursor-pointer items-center gap-2 rounded-xl border p-3 ${sig.language === o.v ? 'border-brand-primary bg-brand-surface/60' : 'border-brand-border'}`}>
              <input type="radio" name="lang" checked={sig.language === o.v}
                onChange={() => setSig((p) => ({ ...p, language: o.v }))} className="h-4 w-4" />
              <span className="text-sm text-brand-ink">{o.l}</span>
            </label>
          ))}
        </div>
        {sig.language !== 'en' && (
          <p className="mt-2 rounded-lg bg-amber-50 p-2.5 text-xs text-amber-900">
            Hierdie vorm is in Engels. Ons gaan dit in Afrikaans met u deurgaan voordat ons begin,
            en ons teken aan dat u dit in Afrikaans verduidelik wou hê.
            <span className="mt-1 block text-amber-800">
              This form is in English. We will go through it with you in Afrikaans before we start,
              and we record that you asked for it in Afrikaans.
            </span>
          </p>
        )}

        <label className="mt-4 block text-sm font-medium text-brand-ink">
          Is someone helping you with the language? <span className="font-normal text-brand-muted">(optional)</span>
        </label>
        <input type="text" value={sig.interpreter_name}
          onChange={(e) => setSig((p) => ({ ...p, interpreter_name: e.target.value }))}
          placeholder="Their name, if someone is interpreting for you"
          className="mt-1 w-full rounded-xl border border-brand-border p-3 text-base" />

        <label className="mt-3 block text-sm font-medium text-brand-ink">Who is signing?</label>
        <div className="mt-2 space-y-2">
          {CAPACITIES.map((c) => (
            <label key={c.value} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 ${sig.signer_capacity === c.value ? 'border-brand-primary bg-brand-surface/60' : 'border-brand-border'}`}>
              <input type="radio" name="cap" checked={sig.signer_capacity === c.value}
                onChange={() => setSig((p) => ({ ...p, signer_capacity: c.value }))} className="h-4 w-4" />
              <span className="text-sm text-brand-ink">{c.label}</span>
            </label>
          ))}
        </div>

        {caregiverBlocked && (
          <div className="mt-3 rounded-xl border border-red-300 bg-red-50 p-3 text-xs text-red-800">
            This procedure is a surgical one, and a care-giver cannot consent to surgery — a parent or legal
            guardian has to. Please contact the practice and we will arrange it.
          </div>
        )}

        {sig.signer_capacity !== 'self' && (
          <div className="mt-3">
            <label className="mb-1 block text-sm font-medium text-brand-ink">How are you related to the patient?</label>
            <input type="text" value={sig.signer_relationship} onChange={(e) => setSig((p) => ({ ...p, signer_relationship: e.target.value }))}
              placeholder="e.g. mother, father, legal guardian" className="w-full rounded-xl border border-brand-border p-3 text-base" />
          </div>
        )}

        {co_signature_required && (
          <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3">
            <p className="text-xs font-semibold text-amber-900">
              This patient is between 12 and 18 and the procedure is surgical, so a parent or guardian must sign as well.
            </p>
            <input type="text" value={sig.co_signer_name} onChange={(e) => setSig((p) => ({ ...p, co_signer_name: e.target.value }))}
              placeholder="Parent or guardian’s full name" className="mt-2 w-full rounded-xl border border-amber-300 p-3 text-base" />
          </div>
        )}

        <div className="mt-3">
          <label className="mb-1 block text-sm font-medium text-brand-ink">Your full name</label>
          <input type="text" value={sig.signed_name} autoComplete="name" onChange={(e) => setSig((p) => ({ ...p, signed_name: e.target.value }))}
            placeholder="First name and surname" className="w-full rounded-xl border border-brand-border p-3 text-base" />
          <p className="mt-1 text-xs text-brand-muted">Typing your name here is what signs this form.</p>
        </div>

        <div className="mt-3">
          <label className="mb-1 block text-sm font-medium text-brand-ink">Your ID or passport number <span className="text-brand-muted">(optional)</span></label>
          <input type="text" value={sig.signer_id_number} onChange={(e) => setSig((p) => ({ ...p, signer_id_number: e.target.value }))}
            className="w-full rounded-xl border border-brand-border p-3 text-base" />
        </div>

        <div className="mt-3">
          <label className="mb-1 block text-sm font-medium text-brand-ink">Signature</label>
          <SignaturePad onChange={({ dataUrl, metrics }) => { setStroke(dataUrl); setStrokeMetrics(metrics) }} />
        </div>

        {error && <p className="mt-3 rounded-lg bg-red-50 p-3 text-sm font-medium text-red-700">{error}</p>}

        <button onClick={submit} disabled={busy}
          className="mt-4 w-full rounded-xl bg-brand-primary p-4 text-base font-semibold text-white disabled:opacity-60">
          {busy ? 'Signing…' : 'Sign and send'}
        </button>

        <p className="mt-3 text-[11px] leading-relaxed text-brand-muted">
          {practice.name} records the date and time you signed and keeps a copy in your file. You can ask us for a copy,
          or change your mind about the treatment, at any time.
        </p>
      </div>
    </Shell>
  )
}
